import Image from 'next/image'

const PAINTINGS_BASE = 'https://cdn.soku.ai/marketing/images/soku-ai/paintings'

/**
 * The luminist paintings the homepage is built on. Every panel on the page
 * pulls from this one map so the art direction stays a single decision.
 */
export const paintings = {
  heroSkyDusk: `${PAINTINGS_BASE}/hero-sky-dusk.jpg`,
  rowboatPredawn: `${PAINTINGS_BASE}/rowboat-predawn.jpg`,
  sailboatSunrise: `${PAINTINGS_BASE}/sailboat-sunrise.jpg`,
  balloonSunrise: `${PAINTINGS_BASE}/balloon-sunrise.jpg`,
  lanternSea: `${PAINTINGS_BASE}/lantern-sea.jpg`,
  marshEstuary: `${PAINTINGS_BASE}/marsh-estuary.jpg`,
  agencyFleet: `${PAINTINGS_BASE}/agency-fleet.jpg`,
  forestSunbeams: `${PAINTINGS_BASE}/forest-sunbeams.jpg`,
} as const

export const eyebrowClassName =
  'inline-flex items-center gap-2 rounded-full border border-black/[0.07] bg-white px-3.5 py-1.5 text-xs font-medium text-foreground shadow-[0_1px_2px_rgba(13,20,36,0.06)]'

/** The small pill that labels each band ("The Problem", "Growth Channels"). */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className={eyebrowClassName}>
      <span className="bg-primary size-2 rounded-[2px]" aria-hidden />
      {children}
    </span>
  )
}

type PaintingPanelProps = {
  src: string
  className?: string
  sizes: string
  /** Darkens the painting so white type stays legible on top of it. */
  scrim?: 'none' | 'soft' | 'strong'
  priority?: boolean
  children?: React.ReactNode
}

const scrimClassName = {
  none: '',
  soft: 'bg-[linear-gradient(180deg,rgba(9,14,28,0.32)_0%,rgba(9,14,28,0.06)_45%,rgba(9,14,28,0.34)_100%)]',
  // Weighted to the top half: the cards that use it set their copy against the
  // upper edge, and the SEO card's pale marsh sky needs the extra cover.
  strong:
    'bg-[linear-gradient(180deg,rgba(9,14,28,0.66)_0%,rgba(9,14,28,0.52)_45%,rgba(9,14,28,0.38)_72%,rgba(9,14,28,0.68)_100%)]',
} as const

/**
 * A painting cropped into a rounded plate, with the film grain and an optional
 * scrim. Children render above both layers — this is what the floating product
 * cards sit on.
 */
export function PaintingPanel({
  src,
  className = '',
  sizes,
  scrim = 'none',
  priority = false,
  children,
}: PaintingPanelProps) {
  return (
    <div
      className={`luminism-panel relative rounded-2xl bg-[#1d2740] ring-1 ring-black/[0.06] ${className}`}
    >
      <Image
        src={src}
        alt=""
        fill
        sizes={sizes}
        priority={priority}
        className="object-cover"
      />
      {scrim !== 'none' && (
        <div
          className={`absolute inset-0 z-[1] ${scrimClassName[scrim]}`}
          aria-hidden
        />
      )}
      {children}
    </div>
  )
}

/**
 * Frosted card used for the mock product surfaces that float over a painting.
 * Kept near-opaque on purpose: a genuinely translucent card over a busy
 * painting eats the text contrast the design depends on.
 */
export function GlassCard({
  className = '',
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={`rounded-xl bg-white/90 shadow-[0_10px_26px_rgba(13,20,36,0.22)] ring-1 ring-white/60 backdrop-blur-md ${className}`}
    >
      {children}
    </div>
  )
}

/**
 * The dusk-sky backdrop a marketing hero sits on.
 *
 * Replaces the `bg-black` + `bg-neutral-950` + white-radial + old `hero-bg.png`
 * + `bg-black/40` stack that was copy-pasted into the shared solution page,
 * /agencies and /pricing. Put it inside a `relative overflow-hidden` section
 * and render the content above it with `relative z-10`.
 */
export function LuministHeroBackdrop({ band }: { band?: string }) {
  return (
    // `band` caps the painting to a top strip that dissolves into the section's
    // ink. Tall heroes (/pricing carries its plan cards inside the hero) would
    // otherwise scale a 16:9 painting to cover a portrait box, leaving a narrow
    // vertical slice that reads as flat navy rather than as a sky.
    <div
      className={`luminism-panel absolute inset-x-0 top-0 ${band ?? 'bottom-0'}`}
      aria-hidden
    >
      <Image
        src={paintings.heroSkyDusk}
        alt=""
        fill
        sizes="100vw"
        className="object-cover object-[50%_65%]"
        priority
        quality={88}
      />
      {/* Sink the top and bottom edges so a transparent header and any card
          near the fold keep their contrast against the bright horizon band. */}
      <div
        className={
          band
            ? 'absolute inset-0 bg-[linear-gradient(180deg,rgba(9,14,28,0.55)_0%,rgba(9,14,28,0.18)_34%,rgba(13,20,36,0.86)_82%,rgba(13,20,36,1)_100%)]'
            : 'absolute inset-0 bg-[linear-gradient(180deg,rgba(9,14,28,0.55)_0%,rgba(9,14,28,0.18)_38%,rgba(9,14,28,0.62)_100%)]'
        }
      />
    </div>
  )
}

export function ArrowIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4.167 10h11.666M10 4.167 15.833 10 10 15.833"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
